import { type ConfigureStoreOptions, configureStore } from '@reduxjs/toolkit'
import { enableMapSet } from 'immer'
import { apiConfig, configuration, middlewares, queryReducers } from 'mmfintech-backend-api'
import { Analytics, MetaPixelCode, TwitterConversionTracking } from 'mmfintech-commons'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import ThemeContextProvider from './context/ThemeContext'

import './i18n'

import '@scss/vendor.css'
import 'react-phone-input-2/lib/style.css'
import 'react-datepicker/dist/react-datepicker.css'
import 'react-pdf/dist/Page/TextLayer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'mmfintech-portal-commons/dist/main.css'
import './index.css'
import '@scss/app.scss'

import { pdfjs } from 'react-pdf'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

enableMapSet()

export const createStore = (options?: ConfigureStoreOptions['preloadedState'] | undefined) =>
  configureStore({
    reducer: queryReducers,
    middleware: getDefaultMiddleware => getDefaultMiddleware().concat(middlewares.apiMiddleware),
    ...options
  })

export const store = createStore()

export type RootState = ReturnType<typeof store.getState>
export type AppStore = ReturnType<typeof createStore>

export type AppDispatch = AppStore['dispatch']

const d = document

if (configuration.isProduction()) {
  const s2 = d.createElement('script')
  s2.type = 'text/javascript'
  s2.src = 'https://l.getsitecontrol.com/g4d2585w.js'
  s2.async = true
  d.getElementsByTagName('head')[0].appendChild(s2)
}

const container = document.getElementById('kingdom-app')
const root = createRoot(container)

root.render(
  <>
    <Analytics
      enabled={configuration.isProduction()}
      codes={[apiConfig.VITE_GA_CODE as string, apiConfig.VITE_GTM_CODE as string]}
    />
    <TwitterConversionTracking
      enabled={configuration.isProduction()}
      code={apiConfig.VITE_TWITTER_TRACKING_CODE as string}
    />
    <MetaPixelCode enabled={configuration.isProduction()} code={apiConfig.VITE_META_PIXEL_CODE as string} />
    <Provider store={store}>
      <BrowserRouter>
        <ThemeContextProvider>
          <App />
        </ThemeContextProvider>
      </BrowserRouter>
    </Provider>
  </>
)
